import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { PatchDetection } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { StackedCountSeries } from '../charts'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime, formatDay } from '../common'
import { DataTable, type Column } from '../DataTable'
import { KevChip, patchKindLabel, severityLabel, SeverityChip } from './labels'

const PERIODS = [30, 90, 180, 365] as const
const SEVERITA = ['critica', 'alta', 'media', 'bassa'] as const
const FASCE = [
  { k: '0_15', l: '0–15' }, { k: '16_30', l: '16–30' }, { k: '31_90', l: '31–90' },
  { k: '91_180', l: '91–180' }, { k: 'over_180', l: '> 180' },
] as const

/**
 * The life of each missing patch (patch_lifecycle_service): how many opened,
 * were fixed and came back in the period, the mean time to remediate by
 * severity, new against fixed week by week, the open ones by age, and the
 * detections one by one with their trail.
 */
export function Lifecycle() {
  const [days, setDays] = useState<number>(90)
  const [vista, setVista] = useState<'open' | 'fixed'>('open')
  const [aperta, setAperta] = useState<string | null>(null)
  const state = useAsync(() => api.patchLifecycle(days), [days])
  const righe = useAsync(() => api.patchDetections(vista === 'open' ? { status: 'open' } : { status: 'fixed', days }), [vista, days])
  const d = state.data
  const tutte = d?.mttr['*']

  return (
    <>
      <Card title={t('Ciclo di vita delle patch')}>
        <div className="patch-toolbar">
          <div className="segmented" role="group" aria-label={t('Periodo')}>
            {PERIODS.map((p) => (
              <button key={p} type="button" className={days === p ? 'segmented__on' : ''} onClick={() => setDays(p)}>
                {p === 365 ? t('Un anno') : t('{n} giorni', { n: p })}
              </button>
            ))}
          </div>
        </div>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Ogni patch mancante su ogni macchina, dalla prima volta che manca a quando è corretta: una patch è corretta quando resta assente per 12 ore (una scansione senza credenziali non conta), datata da quando è sparita; se torna, si riapre. Da qui il tempo medio di correzione (MTTR) e le nuove contro le corrette.')}
        </p>
        <ErrorBanner error={state.error} />
        {!d ? !state.error && <Spinner /> : (
          <div className="stats">
            <StatCard label={t('Nuove')} value={d.new} />
            <StatCard label={t('Corrette')} value={d.fixed} tone="ok" />
            <StatCard label={t('Riaperte')} value={d.reopened} tone={d.reopened ? 'warn' : 'ok'} />
            {(d.superseded ?? 0) > 0 && (
              <StatCard label={t('Superate')} value={d.superseded ?? 0}
                        hint={t('KB cumulative sostituite da una più nuova: non contano come corrette')} />
            )}
            <StatCard label={t('Aperte ora')} value={d.open} hint={t('su {n} macchine', { n: d.open_machines })} />
            <StatCard label={t('MTTR')} value={tutte?.mean != null ? t('{n} giorni', { n: tutte.mean }) : '—'}
                      hint={tutte?.median != null ? t('mediana {n} giorni', { n: tutte.median }) : undefined} />
          </div>
        )}
      </Card>

      {d && (
        <div className="grid grid--2" style={{ alignItems: 'start', gap: 16 }}>
          <Card title={t('Nuove e corrette, per settimana')}>
            <StackedCountSeries
              points={d.weeks.map((w) => ({ date: w.week, values: { detected: w.detected, reopened: w.reopened, fixed: w.fixed } }))}
              series={[
                { key: 'detected', label: t('nuove'), color: 'var(--serie-1)' },
                { key: 'reopened', label: t('riaperte'), color: 'var(--serie-3)' },
                { key: 'fixed', label: t('corrette'), color: 'var(--ok)' },
              ]}
              label={t('Nuove e corrette, per settimana')}
            />
          </Card>
          <Card title={t('Tempo di correzione per gravità')}>
            <table className="table">
              <thead><tr><th>{t('Gravità')}</th><th>{t('Corrette')}</th><th>{t('Media (giorni)')}</th><th>{t('Mediana (giorni)')}</th><th>{t('Entro lo SLA')}</th></tr></thead>
              <tbody>
                {SEVERITA.map((s) => {
                  const m = d.mttr[s]
                  return (
                    <tr key={s}>
                      <td><SeverityChip severity={s} /></td>
                      <td>{m?.count ?? 0}</td>
                      <td>{m?.mean ?? '—'}</td>
                      <td>{m?.median ?? '—'}</td>
                      <td>{m && m.count ? `${Math.round((100 * m.within_sla) / m.count)} %` : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <h4 style={{ margin: '16px 0 6px' }}>{t('Aperte per età (giorni)')}</h4>
            <table className="table">
              <thead><tr><th>{t('Gravità')}</th>{FASCE.map((f) => <th key={f.k}>{f.l}</th>)}</tr></thead>
              <tbody>
                {SEVERITA.map((s) => (
                  <tr key={s}>
                    <td>{severityLabel(s)}</td>
                    {FASCE.map((f) => <td key={f.k}>{d.aging[s]?.[f.k] ?? 0}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      <Card title={t('Rilevamenti')}>
        <div className="patch-toolbar">
          <div className="segmented" role="group" aria-label={t('Quali')}>
            <button type="button" className={vista === 'open' ? 'segmented__on' : ''} onClick={() => setVista('open')}>{t('Aperte')}</button>
            <button type="button" className={vista === 'fixed' ? 'segmented__on' : ''} onClick={() => setVista('fixed')}>{t('Corrette nel periodo')}</button>
          </div>
        </div>
        <ErrorBanner error={righe.error} />
        {!righe.data ? !righe.error && <Spinner /> : righe.data.length === 0
          ? <EmptyState title={vista === 'open' ? t('Nessuna patch mancante aperta.') : t('Nessuna correzione nel periodo.')} />
          : <TabellaRilevamenti righe={righe.data} onApri={setAperta} />}
      </Card>
      {aperta && <DettaglioRilevamento id={aperta} onClose={() => setAperta(null)} />}
    </>
  )
}

/** The detections of a list (the tenant's, or one machine's). */
export function TabellaRilevamenti({ righe, onApri, conMacchina = true }: {
  righe: PatchDetection[]
  onApri: (id: string) => void
  conMacchina?: boolean
}) {
  const colonne: Column<PatchDetection>[] = [
    ...(conMacchina ? [{
      key: 'ci_name', header: t('Macchina'), value: (r: PatchDetection) => r.ci_name,
      render: (r: PatchDetection) => (r.ci_id ? <Link to={`/cis/${r.ci_id}`}>{r.ci_name || r.ci_id.slice(0, 8)}</Link> : <span className="muted">{r.ci_name}</span>),
    }] : []),
    {
      key: 'name', header: t('Patch'), value: (r) => r.name || r.patch_key,
      render: (r) => (
        <button type="button" className="link" onClick={() => onApri(r.id)}>
          {r.name || r.patch_key}{r.exploited && <> <KevChip /></>}
        </button>
      ),
    },
    { key: 'kind', header: t('Tipo'), value: (r) => patchKindLabel(r.kind) },
    { key: 'severity', header: t('Gravità'), value: (r) => severityLabel(r.severity), render: (r) => <SeverityChip severity={r.severity} /> },
    { key: 'cves', header: t('CVE'), value: (r) => r.cves.join(' '), render: (r) => (r.cves.length > 2 ? `${r.cves.slice(0, 2).join(', ')} +${r.cves.length - 2}` : r.cves.join(', ')) },
    { key: 'status', header: t('Stato'), value: (r) => statoLabel(r) },
    { key: 'opened_at', header: t('Aperta dal'), type: 'date', value: (r) => r.opened_at, render: (r) => formatDay(r.opened_at) },
    { key: 'fixed_at', header: t('Corretta il'), type: 'date', value: (r) => r.fixed_at, render: (r) => (r.fixed_at ? formatDay(r.fixed_at) : '—') },
    { key: 'days_open', header: t('Giorni'), value: (r) => r.days_open },
    { key: 'reopened_count', header: t('Riaperture'), value: (r) => r.reopened_count },
  ]
  return <DataTable rows={righe} columns={colonne} rowKey={(r) => r.id} conteggio={{ nome: t('rilevamenti') }} />
}

function statoLabel(r: PatchDetection): string {
  if (r.status === 'fixed') return r.fixed_by_task_id ? t('corretta da CENSO') : t('corretta')
  if (r.status === 'removed') return t('macchina eliminata')
  if (r.status === 'superseded') return t('superata da {kb}', { kb: (r.superseded_by ?? '').replace(/^kb:/, '') })
  if (r.missing_since) return t('assente, in conferma')
  return r.reopened_count ? t('riaperta') : t('aperta')
}

function eventoLabel(e: string): string {
  switch (e) {
    case 'detected': return t('rilevata')
    case 'reopened': return t('riaperta')
    case 'fixed': return t('corretta')
    case 'removed': return t('macchina eliminata')
    case 'superseded': return t('superata da una cumulativa più nuova')
    default: return e
  }
}

/** One detection with its trail: found then, fixed then, by which intervention. */
export function DettaglioRilevamento({ id, onClose }: { id: string; onClose: () => void }) {
  const d = useAsync(() => api.patchDetection(id), [id])
  const r = d.data
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Rilevamento')}>
      <div className="dob-modal__scrim" onClick={onClose} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{r ? `${r.name || r.patch_key} · ${r.ci_name}` : t('Rilevamento')}</h3>
          </div>
          <button type="button" className="secondary" onClick={onClose}>{t('Chiudi')}</button>
        </div>
        <ErrorBanner error={d.error} />
        {!r ? !d.error && <Spinner /> : (
          <>
            <dl className="kv">
              <dt>{t('Gravità')}</dt><dd><SeverityChip severity={r.severity} />{r.exploited && <> <KevChip /></>}</dd>
              <dt>{t('CVE')}</dt><dd>{r.cves.join(', ') || '—'}</dd>
              <dt>{t('Prima volta')}</dt><dd>{formatDateTime(r.first_detected_at)}</dd>
              <dt>{t('Stato')}</dt><dd>{statoLabel(r)}{r.status !== 'open' && r.fixed_at && <> · {formatDateTime(r.fixed_at)}</>}</dd>
              <dt>{t('Giorni aperta')}</dt><dd>{r.days_open}</dd>
              {r.fixed_by && (
                <>
                  <dt>{t('Installata da')}</dt>
                  <dd>
                    {t('intervento di CENSO')} · {t('richiesto da {chi}', { chi: r.fixed_by.requested_by || '—' })}
                    {r.fixed_by.approved_by && <> · {t('approvato da {chi} il {quando}', { chi: r.fixed_by.approved_by, quando: formatDateTime(r.fixed_by.approved_at) })}</>}
                    {r.fixed_by.applied_at && <> · {t('installato il {quando}', { quando: formatDateTime(r.fixed_by.applied_at) })}</>}
                  </dd>
                </>
              )}
            </dl>
            <h4 style={{ margin: '12px 0 6px' }}>{t('Cronologia')}</h4>
            <table className="table">
              <thead><tr><th>{t('Quando')}</th><th>{t('Evento')}</th><th>{t('Gravità')}</th><th>{t('Giorni aperta')}</th></tr></thead>
              <tbody>
                {r.events.map((e, i) => (
                  <tr key={i}><td>{formatDateTime(e.at)}</td><td>{eventoLabel(e.event)}</td><td>{severityLabel(e.severity)}</td><td>{e.days_open ?? '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  )
}

/** One machine's history, for its Patching section. */
export function StoriaPatchMacchina({ ciId }: { ciId: string }) {
  const righe = useAsync(() => api.patchDetections({ ci_id: ciId }), [ciId])
  const [aperta, setAperta] = useState<string | null>(null)
  if (righe.error) return <ErrorBanner error={righe.error} />
  if (!righe.data) return <Spinner />
  if (righe.data.length === 0) return <p className="muted">{t('Nessuna patch mancante registrata su questa macchina.')}</p>
  return (
    <>
      <TabellaRilevamenti righe={righe.data} onApri={setAperta} conMacchina={false} />
      {aperta && <DettaglioRilevamento id={aperta} onClose={() => setAperta(null)} />}
    </>
  )
}
